import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  ArrowRight, 
  Copy, 
  ExternalLink, 
  CheckCircle, 
  Clock,
  Zap,
  Hash,
  User,
  Coins
} from 'lucide-react';
import { Web3 } from 'web3';

const Container = styled.div`
  background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
  min-height: 100vh;
  color: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  max-width: 600px;
  margin: 0 auto 2rem auto;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(6, 182, 212, 0.3), 0 0 40px rgba(6, 182, 212, 0.1);
  
  @media (min-width: 768px) {
    margin: 0 auto 3rem auto;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.875rem;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 0.875rem;
  outline: none;
  
  &::placeholder {
    color: #64748B;
  }
  
  @media (min-width: 768px) {
    padding: 1rem;
    font-size: 1rem;
  }
`;

const SearchButton = styled.button`
  background: #3B82F6;
  color: white;
  border: none;
  padding: 0.875rem 1.25rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
  font-size: 0.875rem;
  
  &:hover {
    background: #2563EB;
  }
  
  @media (min-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1rem;
  }
`;

const TransactionCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  backdrop-filter: blur(10px);
  margin-bottom: 0;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  
  @media (min-width: 768px) {
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 0;
  }
`;

const TransactionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  padding-right: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
    padding-right: 0;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (min-width: 768px) {
    gap: 1rem;
  }
`;

const StatusBadge = styled.div`
  background: ${props => props.success ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'};
  color: ${props => props.success ? '#10B981' : '#EF4444'};
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  width: fit-content;
  
  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    gap: 0.5rem;
  }
`;

const TransactionTitle = styled.h1`
  font-size: 1.125rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  gap: 0;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  @media (min-width: 768px) {
    grid-template-columns: 200px 1fr;
    gap: 1rem;
  }
`;

const DetailLabel = styled.div`
  color: #94A3B8;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  
  @media (min-width: 768px) {
    font-size: 0.875rem;
  }
`;

const DetailValue = styled.div`
  color: #ffffff;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.75rem;
  word-break: break-all;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
  
  @media (min-width: 768px) {
    font-size: 0.875rem;
    align-items: center;
  }
`;

const AddressLink = styled.span`
  color: #3B82F6;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #EF4444;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
  line-height: 1;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }
  
  @media (min-width: 768px) {
    top: 1rem;
    right: 1rem;
    width: 32px;
    height: 32px;
    font-size: 1.2rem;
  }
`;

const CopyButton = styled.button`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #3B82F6;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    background: rgba(59, 130, 246, 0.2);
  }
  
  @media (min-width: 768px) {
    font-size: 0.75rem;
    gap: 0.375rem;
  }
`;

const ValueBadge = styled.span`
  background: rgba(6, 182, 212, 0.2);
  color: #06B6D4;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.8125rem;
  white-space: nowrap;
  
  @media (min-width: 768px) {
    padding: 0.25rem 0.75rem;
    font-size: 0.875rem;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #94A3B8;
  font-size: 0.9375rem;
  
  @media (min-width: 768px) {
    padding: 3rem;
    font-size: 1rem;
  }
`;

const ErrorState = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  color: #EF4444;
  max-width: 600px;
  margin: 0 auto;
  font-size: 0.9375rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
    font-size: 1rem;
  }
`;

const HashText = styled.span`
  display: block;
  
  @media (max-width: 767px) {
    word-break: break-all;
  }
`;

const TransactionDetails = ({ transactionData, searchQuery, onBack }) => {
  // Remove all the search logic - just display the data

  const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    // Optional: show a toast notification that it was copied
    console.log('Copied to clipboard:', text);
  }).catch(err => {
    console.error('Failed to copy:', err);
  });
};

const formatValue = (value) => {
  if (!value || value === '0') return '0 HYX';
  return `${Web3.utils.fromWei(value, 'ether')} HYX`;
};
  
  return (
    <Container>
      <ContentWrapper>
        
        {transactionData ? (
          <TransactionCard>
            <CloseButton onClick={onBack}>×</CloseButton>
            <TransactionHeader>
              <HeaderTop>
                <Hash size={20} color="#3B82F6" />
                <TransactionTitle>Transaction Details</TransactionTitle>
              </HeaderTop>
              <StatusBadge success={transactionData.receipt?.status}>
                <CheckCircle size={14} />
                {transactionData.receipt?.status ? 'Success' : 'Failed'}
              </StatusBadge>
            </TransactionHeader>

            <DetailsGrid>
              <DetailRow>
                <DetailLabel>
                  <Hash size={14} />
                  Transaction Hash
                </DetailLabel>
                <DetailValue>
                  <HashText>{transactionData.hash}</HashText>
                  <CopyButton onClick={() => copyToClipboard(transactionData.hash)}>
                    <Copy size={10} /> Copy
                  </CopyButton>
                </DetailValue>
              </DetailRow>

              <DetailRow>
                <DetailLabel>
                  <Hash size={14} />
                  Block Number
                </DetailLabel>
                <DetailValue>
                  {transactionData.blockNumber?.toString()}
                </DetailValue>
              </DetailRow>

              <DetailRow>
                <DetailLabel>
                  <Clock size={14} />
                  Timestamp
                </DetailLabel>
                <DetailValue>
                  {new Date(Number(transactionData.timestamp) * 1000).toLocaleString()}
                </DetailValue>
              </DetailRow>

              <DetailRow>
                <DetailLabel>
                  <User size={14} />
                  From
                </DetailLabel>
                <DetailValue>
                  <HashText>{transactionData.from}</HashText>
                  <CopyButton onClick={() => copyToClipboard(transactionData.from)}>
                    <Copy size={10} /> Copy
                  </CopyButton>
                </DetailValue>
              </DetailRow>

              <DetailRow>
                <DetailLabel>
                  <ArrowRight size={14} />
                  To
                </DetailLabel>
                <DetailValue>
                  <HashText>{transactionData.to || 'Contract Creation'}</HashText>
                  {transactionData.to && (
                    <CopyButton onClick={() => copyToClipboard(transactionData.to)}>
                      <Copy size={10} /> Copy
                    </CopyButton>
                  )}
                </DetailValue>
              </DetailRow>

              <DetailRow>
                <DetailLabel>
                  <Coins size={14} />
                  Value
                </DetailLabel>
                <DetailValue>
                  <ValueBadge>{formatValue(transactionData.value)}</ValueBadge>
                </DetailValue>
              </DetailRow>

              <DetailRow>
                <DetailLabel>
                  <Zap size={14} />
                  Gas Used
                </DetailLabel>
                <DetailValue>
                  {transactionData.receipt?.gasUsed?.toString()} / {transactionData.gas?.toString()}
                </DetailValue>
              </DetailRow>

              <DetailRow>
                <DetailLabel>
                  <Zap size={14} />
                  Gas Price
                </DetailLabel>
                <DetailValue>
                  {Web3.utils.fromWei(transactionData.gasPrice || '0', 'gwei')} Gwei
                </DetailValue>
              </DetailRow>
            </DetailsGrid>
          </TransactionCard>
        ) : (
          <LoadingState>No transaction data</LoadingState>
        )}
      </ContentWrapper>
    </Container>
  );
};

export default TransactionDetails;